import React, { FC, useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ApiV2, Code } from '@.api';
import { PaginationType } from '@.api/methods';
import { Board } from '@.partials';
import { Filter3, Pagination } from '@.ui';
import { createDefaultPagination } from '@/Lib';
import { ListClusterDashboardStoresQuery } from '@/Services/api/methods/v2/serviceProviderClusters';
import { SpClusterDashboardStoreResource } from '@/Types/serviceProviderClusters';

import { StoreCard } from './components';
import S from './StoreClusterDetail.module.scss';

interface StoreClusterDashboardProps {
    spStoreClusterId: number;
}

export const StoreClusterDashboard: FC<StoreClusterDashboardProps> = ({ spStoreClusterId }) => {
    const [searchParams] = useSearchParams();

    const [clusterStores, setClusterStores] = useState<SpClusterDashboardStoreResource[]>([]);
    const [pagination, setPagination] = useState<PaginationType>(createDefaultPagination);

    const listClusterQueryFromSearchParams = (searchParams: URLSearchParams): Omit<ListClusterDashboardStoresQuery, 'page' | 'per_page'> => {
        const companiesIds = searchParams.get('company_id');
        const storeGroupsIds = searchParams.get('store_group_id');
        const storesIds = searchParams.get('store_id');

        return {
            companies_ids: companiesIds || undefined,
            store_groups_ids: storeGroupsIds || undefined,
            stores_ids: storesIds || undefined,
        };
    };

    const fetchClusterStores = useCallback(
        async (spStoreClusterId: number, page: number, query: Omit<ListClusterDashboardStoresQuery, 'page' | 'per_page'> | undefined) => {
            const response = await ApiV2.serviceProviderClusters().listClusterDashboardStores(Number(spStoreClusterId), {
                page,
                per_page: createDefaultPagination().per_page,
                ...query,
            });

            if (response.code === Code.Success) {
                return response.data;
            }

            return null;
        },
        []
    );

    useEffect(() => {
        const setInitialClusterStores = async () => {
            const response = await fetchClusterStores(spStoreClusterId, createDefaultPagination().page, listClusterQueryFromSearchParams(searchParams));

            if (response) {
                setClusterStores(response.stores.items);
                setPagination(response.stores.pagination);
            }
        };

        setInitialClusterStores();
    }, [spStoreClusterId, fetchClusterStores, searchParams]);

    const onChangePage = async (page: number) => {
        const response = await fetchClusterStores(spStoreClusterId, page, listClusterQueryFromSearchParams(searchParams));

        if (response) {
            setClusterStores(response.stores.items);
            setPagination(response.stores.pagination);
        }
    };

    return (
        <div className={S.content}>
            <Board.Item>
                <h1>Store cluster</h1>
                <Filter3>
                    <Filter3.Companies related />
                    <Filter3.StoreGroups related />
                    <Filter3.Stores related />
                </Filter3>
            </Board.Item>

            <div className={S.stores}>
                {clusterStores.map((store) => {
                    return <StoreCard key={store.id} store={store} />;
                })}
            </div>

            <Pagination current={pagination.page} last={pagination.last_page} changePage={onChangePage} />
        </div>
    );
};
